import React, { useState } from "react";
import { Card, Grid, IconButton, Tooltip } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MainLayout from "../../examples/LayoutContainers/MainLayout";
import MainNavbar from "../../examples/Navbars/MainNavbar";
import DataTable from "../../examples/Tables/DataTable";
import BrandSelect from "../../components/BrandSelect";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { Permissions } from "../../constants/roles";
import { hasPermission } from "../../utils/roles";
import { toastError, toastSuccess } from "../../utils/toast";
import { useGetBrands } from "../ApiKeyManagement/hooks";
import WebhookFormModal from "./components/WebhookFormModal";
import DeleteWebhook from "./components/DeleteWebhook";

/** Manages the default payment webhook for the selected brand. */
const Webhooks = () => {
  const { data: brands = [], isLoading, isError } = useGetBrands();
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const brand = brands.find((item) => item._id === selectedBrandId) || brands[0];
  const canManage = hasPermission(Permissions.BRAND.UPDATE_SETTINGS);
  const hasWebhook = Boolean(brand?.notificationUrl);

  /** Copies the selected brand's signing key and reports clipboard failures. */
  const handleCopySignatureKey = async () => {
    try {
      await navigator.clipboard.writeText(brand.signKey);
      toastSuccess({ description: "Signature key copied successfully" });
    } catch {
      toastError({ description: "Unable to copy signature key. Please copy it manually." });
    }
  };

  const rows = hasWebhook
    ? [
        {
          notificationUrl: (
            <SoftTypography
              component="p"
              variant="button"
              fontWeight="regular"
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {brand.notificationUrl}
            </SoftTypography>
          ),
          actions: canManage && (
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              gap={1}
            >
              <WebhookFormModal brand={brand} />
              <DeleteWebhook brand={brand} />
            </SoftBox>
          ),
        },
      ]
    : [];

  return (
    <MainLayout>
      <MainNavbar />
      <SoftBox key={brand?._id}>
        <SoftTypography
          component="p"
          variant="caption"
          color="text"
          sx={{ mb: 2, lineHeight: 1.5 }}
        >
          Set a default webhook URL{brands.length > 1 ? " for each brand" : ""}. A webhook URL
          included in a payment payload overrides the URL configured here for that payment.
        </SoftTypography>
        {(brands.length > 1 || (brand && !hasWebhook && canManage)) && (
          <Grid
            container
            display="flex"
            justifyContent={brands.length > 1 ? "space-between" : "flex-end"}
            alignItems="flex-end"
            gap={2}
            mb={3}
          >
            <BrandSelect
              brands={brands}
              value={brand ? { value: brand._id, label: brand.name } : null}
              onChange={(option) => setSelectedBrandId(option.value)}
            />
            {brand && !hasWebhook && canManage && <WebhookFormModal brand={brand} />}
          </Grid>
        )}
        {brand && !isLoading && !isError && (
          <Card component="section" aria-label="Signature key" sx={{ p: 3, mb: 2 }}>
            <SoftTypography component="h2" variant="h6" fontWeight="medium">
              Signature key
            </SoftTypography>
            <SoftTypography
              component="p"
              variant="caption"
              color="text"
              sx={{ mt: 0.5, lineHeight: 1.5 }}
            >
              Use this key to verify webhook signatures. It applies to all webhooks for this brand,
              including URLs set in payment payloads.
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" gap={1} mt={1}>
              <SoftTypography
                component="p"
                variant="button"
                fontWeight="regular"
                sx={{ minWidth: 0, overflowWrap: "anywhere" }}
              >
                {brand.signKey || "Not available"}
              </SoftTypography>
              {brand.signKey && (
                <Tooltip title="Copy signature key">
                  <IconButton
                    aria-label="Copy signature key"
                    size="small"
                    onClick={handleCopySignatureKey}
                    sx={{ flexShrink: 0 }}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </SoftBox>
          </Card>
        )}
        {isLoading || isError || !hasWebhook ? (
          <Card sx={{ p: 3 }}>
            <SoftTypography
              component="p"
              variant="button"
              fontWeight="regular"
              color="text"
              role={isError ? "alert" : "status"}
            >
              {isLoading
                ? "Loading webhooks..."
                : isError
                  ? "Unable to load webhooks. Please refresh the page."
                  : !brand
                    ? "No brands available."
                    : "No webhook URL has been set for this brand."}
            </SoftTypography>
          </Card>
        ) : (
          <SoftBox
            sx={{
              "& .data-table-footer": { display: "none" },
              "& .MuiTableContainer-root": { borderRadius: "inherit" },
              "& table": { tableLayout: "fixed" },
              "& th:first-of-type": { width: "auto" },
              "& th:nth-of-type(2), & td:nth-of-type(2)": { width: 250 },
              "& td:first-of-type > div": { width: "100%" },
              // Stack the URL and actions on small screens to keep long URLs readable.
              "@media (max-width: 599px)": {
                "& thead": { display: "none" },
                "& tbody, & tr, & td": { display: "block", width: "100%" },
                "& td": { height: "auto", px: "23px !important" },
                "& td:nth-of-type(2)": { width: "100%" },
                "& td:first-of-type::before": {
                  content: '"Webhook URL"',
                  display: "block",
                  mb: 0.5,
                  fontWeight: 600,
                },
                "& td:last-of-type > div > div": { flexDirection: "row" },
              },
            }}
          >
            <DataTable
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              manualPagination
              table={{
                columns: [
                  { Header: "Webhook URL", accessor: "notificationUrl" },
                  ...(canManage
                    ? [{ Header: "Actions", accessor: "actions", align: "right" }]
                    : []),
                ],
                rows,
              }}
            />
          </SoftBox>
        )}
      </SoftBox>
    </MainLayout>
  );
};

export default Webhooks;
